<template>
    <!-- Admin Broadcast Modal -->
    <div v-if="showBroadcastModal" class="admin-broadcast-overlay" @click.self="closeModal">
        <div class="admin-broadcast-modal">
            <!-- Modal Header -->
            <div class="modal-header">
                <div class="header-icon">
                    <i class="bi bi-broadcast"></i>
                </div>
                <div class="header-content">
                    <h3 class="modal-title">Broadcast Message</h3>
                    <p class="modal-subtitle">{{ title || 'Send announcement to all judges' }} </p>
                </div>
                <button @click="closeModal" class="close-btn">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body">
                <!-- Message Type Selection -->
                <div class="form-section">
                    <label class="section-label">
                        <i class="bi bi-chat-dots-fill"></i>
                        Message Type
                    </label>
                    <div class="type-buttons">
                        <button v-for="type in messageTypes" :key="type.value" @click="selectMessageType(type.value)"
                            class="type-btn" :class="{ active: broadcastData.type === type.value }">
                            <i :class="type.icon"></i>
                            {{ type.label }}
                        </button>
                    </div>
                </div>

                <!-- Priority Selection -->
                <div class="form-section">
                    <label class="section-label">
                        <i class="bi bi-flag-fill"></i>
                        Priority Level
                    </label>
                    <div class="priority-buttons">
                        <button v-for="priority in priorityLevels" :key="priority.value"
                            @click="selectPriority(priority.value)" class="priority-btn"
                            :class="[priority.value, { active: broadcastData.broadcastPriority === priority.value }]">
                            <i :class="priority.icon"></i>
                            {{ priority.label }}
                        </button>
                    </div>
                </div>

                <!-- Message Input -->
                <div class="form-section">
                    <label class="section-label">
                        <i class="bi bi-pencil-fill"></i>
                        Broadcast Message
                    </label>
                    <div class="message-input-container">
                        <textarea v-model="broadcastData.message" class="message-textarea"
                            placeholder="Enter your broadcast message here..." maxlength="500" rows="4"></textarea>
                        <div class="character-count">
                            {{ broadcastData.message.length }}/500
                        </div>
                    </div>
                </div>

                <!-- Additional Options -->
                <div class="form-section">
                    <label class="section-label">
                        <i class="bi bi-gear-fill"></i>
                        Broadcast Options
                    </label>
                    <div class="options-grid">
                        <label class="option-checkbox">
                            <input type="checkbox" v-model="broadcastData.autoDismiss" class="checkbox-input" />
                            <span class="checkmark"></span>
                            <span class="option-label">
                                <i class="bi bi-clock"></i>
                                Auto-dismiss after 8 seconds
                            </span>
                        </label>

                        <label class="option-checkbox">
                            <input type="checkbox" v-model="broadcastData.requireAcknowledge" class="checkbox-input" />
                            <span class="checkmark"></span>
                            <span class="option-label">
                                <i class="bi bi-check-circle"></i>
                                Require acknowledgment
                            </span>
                        </label>

                        <label class="option-checkbox">
                            <input type="checkbox" v-model="broadcastData.hasAction" class="checkbox-input" />
                            <span class="checkmark"></span>
                            <span class="option-label">
                                <i class="bi bi-lightning"></i>
                                Include action button
                            </span>
                        </label>

                        <label class="option-checkbox">
                            <input type="checkbox" v-model="broadcastData.sendToAll" class="checkbox-input" checked />
                            <span class="checkmark"></span>
                            <span class="option-label">
                                <i class="bi bi-people"></i>
                                Send to all judges
                            </span>
                        </label>
                    </div>
                </div>

                <!-- Action Button Configuration -->
                <div v-if="broadcastData.hasAction" class="form-section">
                    <label class="section-label">
                        <i class="bi bi-lightning-fill"></i>
                        Action Button
                    </label>
                    <div class="action-config">
                        <input v-model="broadcastData.actionText" class="action-input"
                            placeholder="Button text (e.g., 'View Scores', 'Open Criteria')" maxlength="30" />
                        <select v-model="broadcastData.actionType" class="action-select">
                            <option value="redirect">Redirect to Page</option>
                            <option value="refresh">Refresh Data</option>
                            <option value="custom">Custom Action</option>
                        </select>
                    </div>
                </div>

                <!-- Preview Section -->
                <div class="form-section preview-section">
                    <label class="section-label">
                        <i class="bi bi-eye-fill"></i>
                        Message Preview
                    </label>
                    <div class="preview-container" :class="broadcastData.broadcastPriority">
                        <div class="preview-header">
                            <div class="preview-icon">
                                <i class="bi bi-megaphone-fill"></i>
                            </div>
                            <div class="preview-title">
                                <span class="preview-type">{{ getMessageTypeLabel(broadcastData.type) }}</span>
                                <span class="preview-priority" :class="broadcastData.broadcastPriority">
                                    {{ getPriorityLabel(broadcastData.broadcastPriority) }}
                                </span>
                            </div>
                        </div>
                        <div class="preview-message">
                            {{ broadcastData.message || 'Your message will appear here...' }}
                        </div>
                        <div class="preview-options">
                            <span v-if="broadcastData.autoDismiss" class="preview-option">
                                <i class="bi bi-clock"></i> Auto-dismiss
                            </span>
                            <span v-if="broadcastData.requireAcknowledge" class="preview-option">
                                <i class="bi bi-check-circle"></i> Requires ACK
                            </span>
                            <span v-if="broadcastData.hasAction" class="preview-option">
                                <i class="bi bi-lightning"></i> Has Action
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer">
                <button @click="closeModal" class="cancel-btn">
                    <i class="bi bi-x-circle"></i>
                    Cancel
                </button>
                <button @click="resetForm" class="reset-btn">
                    <i class="bi bi-arrow-clockwise"></i>
                    Reset
                </button>
                <button @click="sendBroadcast" class="send-btn" :disabled="!broadcastData.message.trim()"
                    :class="{ disabled: !broadcastData.message.trim() }">
                    <i class="bi bi-send-fill"></i>
                    Broadcast Message
                    <div class="btn-glow"></div>
                </button>
            </div>

            <!-- Decorative Elements -->
            <div class="modal-glow"></div>
            <div class="corner-decoration top-left"></div>
            <div class="corner-decoration top-right"></div>
            <div class="corner-decoration bottom-left"></div>
            <div class="corner-decoration bottom-right"></div>
        </div>
    </div>
</template>

<script>
import { reactive } from 'vue';

export default {
    name: 'AdminBroadcastModal',
    props: {
        showBroadcastModal: Boolean,
        title: {
            type: String,
            default: null
        }
    },
    emits: ['close', 'broadcast'],
    setup(props, { emit }) {
        const messageTypes = [
            { value: 'announcement', label: 'Announcement', icon: 'bi bi-megaphone' },
            { value: 'alert', label: 'Alert', icon: 'bi bi-exclamation-triangle' },
            { value: 'update', label: 'Update', icon: 'bi bi-arrow-clockwise' },
            { value: 'reminder', label: 'Reminder', icon: 'bi bi-bell' },
            { value: 'instruction', label: 'Instruction', icon: 'bi bi-list-check' }
        ];

        const priorityLevels = [
            { value: 'low', label: 'Low', icon: 'bi bi-arrow-down' },
            { value: 'normal', label: 'Normal', icon: 'bi bi-dash' },
            { value: 'high', label: 'High', icon: 'bi bi-arrow-up' },
            { value: 'urgent', label: 'Urgent', icon: 'bi bi-exclamation-lg' }
        ];

        const broadcastData = reactive({
            type: 'announcement',
            broadcastPriority: 'normal',
            message: '',
            autoDismiss: true,
            requireAcknowledge: false,
            hasAction: false,
            sendToAll: true,
            actionText: '',
            actionType: 'redirect'
        });

        const selectMessageType = (type) => {
            broadcastData.type = type;
        };

        const selectPriority = (priority) => {
            broadcastData.broadcastPriority = priority;
        };

        const getMessageTypeLabel = (type) => {
            return messageTypes.find(t => t.value === type)?.label || 'Announcement';
        };

        const getPriorityLabel = (priority) => {
            return priorityLevels.find(p => p.value === priority)?.label || 'Normal';
        };

        const resetForm = () => {
            Object.assign(broadcastData, {
                type: 'announcement',
                broadcastPriority: 'normal',
                message: '',
                autoDismiss: true,
                requireAcknowledge: false,
                hasAction: false,
                sendToAll: true,
                actionText: '',
                actionType: 'redirect'
            });
        };

        const closeModal = () => {
            emit('close');
        };

        const sendBroadcast = () => {
            if (!broadcastData.message.trim()) return;

            const broadcastPayload = {
                ...broadcastData,
                timestamp: new Date().toISOString(),
                id: Date.now().toString()
            };

            emit('broadcast', broadcastPayload);
            resetForm();
            closeModal();
        };

        return {
            messageTypes,
            priorityLevels,
            broadcastData,
            selectMessageType,
            selectPriority,
            getMessageTypeLabel,
            getPriorityLabel,
            resetForm,
            closeModal,
            sendBroadcast
        };
    }
};
</script>

<style scoped>
/* Modal Overlay */
.admin-broadcast-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
}

/* Main Modal */
.admin-broadcast-modal {
    position: relative;
    background: linear-gradient(135deg, var(--card-bg) 0%, #222 100%);
    border: 2px solid var(--border-gold);
    border-radius: 20px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow:
        0 25px 50px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(212, 175, 55, 0.1),
        0 0 60px rgba(212, 175, 55, 0.2);
    animation: modalAppear 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.modal-glow {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 50% 0%, rgba(212, 175, 55, 0.1) 0%, transparent 50%);
    pointer-events: none;
}

/* Modal Header */
.modal-header {
    display: flex;
    align-items: center;
    padding: 25px 30px 20px;
    border-bottom: 1px solid var(--border-gold);
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%);
    position: sticky;
    top: 0;
    z-index: 10;
}

.header-icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;
    box-shadow: 0 0 20px var(--shadow-gold);
}

.header-icon i {
    color: var(--dark-bg);
    font-size: 1.3rem;
}

.header-content {
    flex: 1;
}

.modal-title {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin: 0 0 5px 0;
}

.modal-subtitle {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin: 0;
    font-weight: 300;
}

.close-btn {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-gold);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.3s ease;
}

.close-btn:hover {
    background: rgba(212, 175, 55, 0.1);
    color: var(--gold-primary);
    border-color: var(--gold-primary);
    transform: rotate(90deg);
}

/* Modal Body */
.modal-body {
    padding: 30px;
}

.form-section {
    margin-bottom: 30px;
}

.section-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-light);
    font-weight: 600;
    margin-bottom: 15px;
    font-size: 1rem;
}

.section-label i {
    color: var(--gold-primary);
}

/* Type Buttons */
.type-buttons {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
}

.type-btn {
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    border-radius: 10px;
    padding: 12px 8px;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    font-size: 0.85rem;
}

.type-btn:hover {
    border-color: var(--gold-primary);
    color: var(--text-light);
}

.type-btn.active {
    background: rgba(212, 175, 55, 0.1);
    border-color: var(--gold-primary);
    color: var(--gold-primary);
    box-shadow: 0 0 15px var(--shadow-gold);
}

.type-btn i {
    font-size: 1.2rem;
}

/* Priority Buttons */
.priority-buttons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}

.priority-btn {
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    border-radius: 10px;
    padding: 12px 8px;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    font-size: 0.85rem;
}

.priority-btn:hover {
    transform: translateY(-2px);
}

.priority-btn.active {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.priority-btn.low {
    border-color: rgba(46, 204, 113, 0.3);
}

.priority-btn.low.active {
    background: rgba(46, 204, 113, 0.1);
    border-color: var(--low-color);
    color: var(--low-color);
}

.priority-btn.normal {
    border-color: rgba(52, 152, 219, 0.3);
}

.priority-btn.normal.active {
    background: rgba(52, 152, 219, 0.1);
    border-color: var(--normal-color);
    color: var(--normal-color);
}

.priority-btn.high {
    border-color: rgba(243, 156, 18, 0.3);
}

.priority-btn.high.active {
    background: rgba(243, 156, 18, 0.1);
    border-color: var(--high-color);
    color: var(--high-color);
}

.priority-btn.urgent {
    border-color: rgba(231, 76, 60, 0.3);
}

.priority-btn.urgent.active {
    background: rgba(231, 76, 60, 0.1);
    border-color: var(--urgent-color);
    color: var(--urgent-color);
    animation: pulse 2s infinite;
}

/* Message Input */
.message-input-container {
    position: relative;
}

.message-textarea {
    width: 100%;
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    border-radius: 12px;
    padding: 15px;
    color: var(--text-light);
    font-size: 1rem;
    resize: vertical;
    transition: all 0.3s ease;
    outline: none;
}

.message-textarea:focus {
    border-color: var(--gold-primary);
    box-shadow: 0 0 20px var(--shadow-gold);
}

.character-count {
    position: absolute;
    bottom: 10px;
    right: 15px;
    color: var(--text-muted);
    font-size: 0.8rem;
}

/* Options Grid */
.options-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
}

.option-checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 10px;
    border-radius: 8px;
    transition: background 0.3s ease;
}

.option-checkbox:hover {
    background: rgba(212, 175, 55, 0.05);
}

.checkbox-input {
    display: none;
}

.checkmark {
    width: 20px;
    height: 20px;
    border: 2px solid var(--border-gold);
    border-radius: 4px;
    position: relative;
    transition: all 0.3s ease;
}

.checkbox-input:checked+.checkmark {
    background: var(--gold-primary);
    border-color: var(--gold-primary);
}

.checkbox-input:checked+.checkmark::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: var(--dark-bg);
    font-size: 0.8rem;
    font-weight: bold;
}

.option-label {
    color: var(--text-light);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 5px;
}

.option-label i {
    color: var(--gold-primary);
}

/* Action Configuration */
.action-config {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 15px;
}

.action-input,
.action-select {
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    border-radius: 8px;
    padding: 12px;
    color: var(--text-light);
    outline: none;
    transition: all 0.3s ease;
}

.action-input:focus,
.action-select:focus {
    border-color: var(--gold-primary);
}

.action-select {
    cursor: pointer;
}

/* Preview Section */
.preview-section {
    border-top: 1px solid var(--border-gold);
    padding-top: 20px;
}

.preview-container {
    background: rgba(0, 0, 0, 0.2);
    border: 2px solid var(--border-gold);
    border-radius: 12px;
    padding: 20px;
    transition: all 0.3s ease;
}

.preview-container.low {
    border-color: rgba(46, 204, 113, 0.5);
}

.preview-container.normal {
    border-color: rgba(52, 152, 219, 0.5);
}

.preview-container.high {
    border-color: rgba(243, 156, 18, 0.5);
}

.preview-container.urgent {
    border-color: rgba(231, 76, 60, 0.5);
    animation: pulse 2s infinite;
}

.preview-header {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
}

.preview-icon {
    width: 40px;
    height: 40px;
    background: rgba(212, 175, 55, 0.1);
    border: 2px solid var(--border-gold);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-icon i {
    color: var(--gold-primary);
}

.preview-title {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.preview-type {
    color: var(--text-light);
    font-weight: 600;
}

.preview-priority {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.preview-priority.low {
    background: rgba(46, 204, 113, 0.2);
    color: var(--low-color);
}

.preview-priority.normal {
    background: rgba(52, 152, 219, 0.2);
    color: var(--normal-color);
}

.preview-priority.high {
    background: rgba(243, 156, 18, 0.2);
    color: var(--high-color);
}

.preview-priority.urgent {
    background: rgba(231, 76, 60, 0.2);
    color: var(--urgent-color);
}

.preview-message {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 15px;
    font-style: italic;
}

.preview-options {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
}

.preview-option {
    color: var(--text-muted);
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 5px;
}

.preview-option i {
    color: var(--gold-primary);
}

/* Modal Footer */
.modal-footer {
    padding: 20px 30px 25px;
    border-top: 1px solid var(--border-gold);
    display: flex;
    gap: 15px;
    background: rgba(0, 0, 0, 0.2);
    position: sticky;
    bottom: 0;
}

.cancel-btn,
.reset-btn,
.send-btn {
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.cancel-btn {
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    color: var(--text-light);
}

.cancel-btn:hover {
    background: rgba(212, 175, 55, 0.1);
    border-color: var(--gold-primary);
    color: var(--gold-primary);
}

.reset-btn {
    background: transparent;
    border: 2px solid var(--border-gold);
    color: var(--text-muted);
}

.reset-btn:hover {
    background: rgba(212, 175, 55, 0.05);
    color: var(--text-light);
}

.send-btn {
    flex: 1;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border: 2px solid transparent;
    color: var(--dark-bg);
    position: relative;
    overflow: hidden;
}

.send-btn:hover:not(.disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(212, 175, 55, 0.4);
}

.send-btn.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.btn-glow {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
}

.send-btn:hover:not(.disabled) .btn-glow {
    left: 100%;
}

/* Corner Decorations */
.corner-decoration {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    opacity: 0.5;
}

.corner-decoration.top-left {
    top: 10px;
    left: 10px;
    border-top: 2px solid var(--gold-primary);
    border-left: 2px solid var(--gold-primary);
    border-top-left-radius: 8px;
}

.corner-decoration.top-right {
    top: 10px;
    right: 10px;
    border-top: 2px solid var(--gold-primary);
    border-right: 2px solid var(--gold-primary);
    border-top-right-radius: 8px;
}

.corner-decoration.bottom-left {
    bottom: 10px;
    left: 10px;
    border-bottom: 2px solid var(--gold-primary);
    border-left: 2px solid var(--gold-primary);
    border-bottom-left-radius: 8px;
}

.corner-decoration.bottom-right {
    bottom: 10px;
    right: 10px;
    border-bottom: 2px solid var(--gold-primary);
    border-right: 2px solid var(--gold-primary);
    border-bottom-right-radius: 8px;
}

/* Animations */
@keyframes modalAppear {
    0% {
        opacity: 0;
        transform: scale(0.8) translateY(50px);
    }

    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .admin-broadcast-overlay {
        padding: 15px;
    }

    .modal-header {
        padding: 20px 25px 15px;
    }

    .modal-body {
        padding: 25px;
    }

    .type-buttons {
        grid-template-columns: repeat(3, 1fr);
    }

    .priority-buttons {
        grid-template-columns: repeat(2, 1fr);
    }

    .options-grid {
        grid-template-columns: 1fr;
    }

    .action-config {
        grid-template-columns: 1fr;
    }

    .modal-footer {
        padding: 15px 25px 20px;
        flex-direction: column;
    }

    .preview-title {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
}

@media (max-width: 480px) {
    .modal-header {
        padding: 15px 20px 10px;
    }

    .modal-body {
        padding: 20px;
    }

    .type-buttons {
        grid-template-columns: 1fr 1fr;
    }

    .priority-buttons {
        grid-template-columns: 1fr;
    }
}
</style>